import { useCallback } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useTenantedClient, useWhoami } from '../stores/org';
import { queryKeys } from './query-cache-keys';

export interface DataProvider {
  provider: string;
  status: 'connected' | 'not_connected';
}

export const useWorkOSDataProviders = (opts?: {
  refetchInterval?: number | false;
}) => {
  const { user } = useAuth();
  const client = useTenantedClient();
  const toolsClient = client.toolsV2;
  const orgId = useWhoami((s) => s.org.id);

  const providersQuery = useSuspenseQuery({
    queryKey: queryKeys.toolsV2.workosDataProviders({ orgId }),
    queryFn: async (): Promise<DataProvider[]> => {
      if (user === null) {
        return [];
      }
      const result = await toolsClient.listWorkosDataProviders({});

      return Assert.assertOk(result);
    },
    staleTime: 0,
    gcTime: 0,
    refetchInterval: opts?.refetchInterval,
  });

  const getAuthorizationUrl = useCallback(
    async (
      provider: string
    ): Promise<{ authorizationUrl: string } | { alreadyConnected: true }> => {
      if (user === null) {
        throw new Error('User not authenticated');
      }
      const result = await toolsClient.getWorkosAuthorizationUrl({
        provider,
      });
      const data = Assert.assertOk(result);
      if (data.alreadyConnected === true) {
        return { alreadyConnected: true };
      }
      if (data.authorizationUrl === undefined) {
        throw new Error('Missing authorization URL');
      }
      return { authorizationUrl: data.authorizationUrl };
    },
    [toolsClient, user]
  );

  const disconnect = useCallback(
    async (provider: string): Promise<void> => {
      if (user === null) {
        throw new Error('User not authenticated');
      }
      const result = await toolsClient.disconnectWorkosProvider({
        provider,
      });
      Assert.assertOk(result);
    },
    [toolsClient, user]
  );

  return {
    providers: providersQuery.data,
    refetchProviders: providersQuery.refetch,
    getAuthorizationUrl,
    disconnect,
  };
};
